/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  CommonFlowState,
  FormActionPipelines,
  FormFlowState,
  ListActionPipelines,
  whenFormInvalid,
} from '@gh/builder-shared';
import { AelEvents, CheckUsage } from '@gh/builder-shared-data';
import { EntityInputDialogConfig, entityInputDialogRoute } from '@gh/common-internal-shared';
import { ActionFlow, getInvalidFields, getRouteInitData, trueAction } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import {
  DialogRouteConfig,
  DialogService,
  NotificationService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { openInNewWindow } from '@gh/core-util';
import { filter$ } from '@gh/rx/operators';

import { first, isEmpty } from 'lodash';

import { SchedulingCpDetailsBusinessService } from '../../resources/scheduling-cp-details-business.service';
import {
  CpStage,
  CpStageCc,
  CpUnusedCc,
  CriticalPathDetails,
  getCpStageId,
  newCpStage,
} from '../../resources/scheduling-cp-details.model';
import { SchedulingCpDetailsFormState } from './scheduling-cp-details-form.service';

@Component({
  standalone: false,
  selector: 'gh-critical-path-details',
  templateUrl: 'scheduling-cp-details.component.html',
  providers: [SchedulingCpDetailsFormState],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SchedulingCpDetailsComponent extends AbstractFormComponent<CriticalPathDetails> implements OnInit {
  private original: CriticalPathDetails;
  private reportUrl: string;

  constructor(private route: ActivatedRoute,
              private changeDetectorRef: ChangeDetectorRef,
              private userContext: BuilderUserContext,
              private navigations: BuilderNavigationService,
              private formPipelines: FormActionPipelines,
              private listPipelines: ListActionPipelines,
              private commonActions: CommonActions,
              private dialogs: DialogService,
              private notifications: NotificationService,
              private business: SchedulingCpDetailsBusinessService,
              public form: SchedulingCpDetailsFormState) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<CriticalPathDetails>> {
    return this.formPipelines.complete({
      validateForm: this.commonActions.validateWithEffect(whenFormInvalid, this.handleError.bind(this)),
      execute: this.commonActions.execute(({ value }) => this.business.save(value)),
      finish: ({ value }: any) => {
        if (!isEmpty(value.affectedDrawRules)) {
          this.notifications.warning('msg_cleared_draw_rules', {
            drawRules: value.affectedDrawRules.join(', '),
          });
        }
        if (value.warningMessage && value.warningMessage.trim() !== '') {
          this.notifications.warning(value.warningMessage);
        }
        this.original = value;
        this.form.build(value);
      },
    });
  }

  ngOnInit(): void {
    const { details, reportUrl } = getRouteInitData(this.route);

    this.reportUrl = reportUrl;
    this.original = details;
    this.form.build(details);
  }

  onShowDeletedChange(showDeleted: boolean): void {
    this.form.setShowDeletedStages(showDeleted);
  }

  onStageAdd(): void {
    this.dialogs.openWithInput(<DialogRouteConfig<any>>entityInputDialogRoute, <EntityInputDialogConfig<CpStage>>{
      titleId: 'lbl_add_critical_path_stage',
      placeholderId: 'lbl_name',
      entity: newCpStage(),
      field: 'name',
      maxLength: 40,
      minHeight: 400,
      required: true,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((stage: any) => this.form.addStage(stage));
  }

  onStageOpen(stage: CpStage): void {
    this.dialogs.openWithInput(<DialogRouteConfig<any>>entityInputDialogRoute, <EntityInputDialogConfig<CpStage>>{
      titleId: 'lbl_edit_critical_path_stage',
      placeholderId: 'lbl_name',
      entity: stage,
      field: 'name',
      maxLength: 40,
      minHeight: 400,
      required: true,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((updatedStage: any) => this.form.updateStage(updatedStage));
  }

  onStageDelete(stage: CpStage): void {
    const flow = this.listPipelines.checkUsageToggleItem({
      messages: {
        confirmUsed: 'msg_critical_path_stage_mark_deleted',
        confirmNotUsed: 'msg_critical_path_stage_delete',
      },
      executes: {
        checkUsage: this.commonActions.execute((state: CommonFlowState<any>) =>
          this.business.checkUsage(state.value)),
        summary: trueAction,
      },
      finish: ({ value }: { value: CheckUsage }) => {
        if (value.deleted) {
          this.form.deleteStage(stage);
        } else {
          this.form.markStageAsDeleted(stage);
          this.form.setShowDeletedStages(true);
        }
      },
    });

    flow.run({ value: stage });
  }

  onStageUndelete(stage: CpStage): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_critical_path_stage_undelete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.undeleteStage(stage));
  }

  onStageMove(from: CpStage, to: CpStage): void {
    this.form.moveStages(from, to);
  }

  onSlabChange(stage: CpStage): void {
    this.form.setAsSlab(stage);
  }

  onSubstitutorChange(stage: CpStage, substitor: ID): void {
    this.form.setSubstitutor(stage, substitor);
  }

  onStageSelected(stage: CpStage | undefined): void {
    this.form.selectStage(stage);
  }

  onFocusedCellChange(): void {
    this.form.resetFocusedCell();
  }

  onCurrentStageContractStartChange(cc: CpStageCc): void {
    this.form.setCurrentStageContractStartCc(cc);
  }

  onCurrentStageCcMove(from: CpStageCc, to: CpStageCc): void {
    this.form.moveCurrentStageCc(from, to);
  }

  onCurrentStageCcRemove(cc: CpStageCc): void {
    this.form.removeCurrentStageCc(cc);
  }

  onCurrentStageCcAdd(cc: CpUnusedCc): void {
    this.form.addCurrentStageCc(cc);
  }

  onOpenReport(): void {
    openInNewWindow(this.reportUrl);
  }

  onReset(): void {
    this.dialogs.confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.build(this.original));
  }

  goToAel(): void {
    this.navigations.goToAel({
      entityId: this.userContext.office!.id,
      entityType: 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedCriticalPath],
      label: 'lbl_menu_critical_path',
    });
  }

  private handleError(): void {
    const { firstInvalidStage, hasNoSlab, hasNoRequiredCostCenter, hasNoRequiredContractStart } = this.form;
    if (firstInvalidStage) {
      this.notifications.error('msg_he_incorrect_data_in_tables');
      const invalidStageField = first(getInvalidFields(firstInvalidStage));
      if (invalidStageField) {
        this.form.selectStageCell(firstInvalidStage, { id: getCpStageId(firstInvalidStage), field: invalidStageField });
      }
    } else if (hasNoSlab) {
      this.notifications.error('msg_critical_path_no_slab');
    } else if (hasNoRequiredCostCenter) {
      this.notifications.error('msg_critical_path_no_cc');
    } else if (hasNoRequiredContractStart) {
      this.notifications.error('msg_critical_path_no_contract_start');
    }
  }

}
